import { useState, useMemo } from 'react';
import { Button, Input, Icon, SlideOutPanel, SlideOutPanelBody, DatePicker } from '@repo/ui';
import type { TrustEligibleMatterDto } from '../types';
import { formatCurrency, formatDate, DateFormat } from '@xtnd/web/shared/utils/format';

export interface MatterSelectionTableProps {
  matters: TrustEligibleMatterDto[];
  selectedMatters: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  editableAmounts?: Record<string, number>;
  onAmountChange?: (matterId: string, amount: number) => void;
  editableDates?: Record<string, { issueDate?: Date; dueDate?: Date }>;
  onDateChange?: (matterId: string, field: 'issueDate' | 'dueDate', date: Date | undefined) => void;
  visibleColumns?: Record<string, boolean>;
  totalRecords?: number; // Total records across all pages
  isSelectAllPages?: boolean; // Whether all pages are selected
  onSelectAllPages?: (selected: boolean) => void; // Callback for selecting all pages
}

type SortField = keyof TrustEligibleMatterDto;
type SortDirection = 'asc' | 'desc' | null;

export function MatterSelectionTable({
  matters,
  selectedMatters,
  onSelectionChange,
  editableAmounts = {},
  onAmountChange,
  editableDates = {},
  onDateChange,
  visibleColumns = {
    matterDisplayNumber: true,
    clientName: true,
    matterStatus: true,
    minimumThreshold: true,
    trustBalance: true,
    outstandingBalance: true,
    wip: true,
    calculatedTrustAmount: true,
    trustRequestAmount: true,
    issueDate: true,
    dueDate: true,
    lastPaymentDate: false,
    lastInvoiceDate: false,
    lastTrustRequestDate: false,
  },
  totalRecords,
  isSelectAllPages = false,
  onSelectAllPages,
}: MatterSelectionTableProps) {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedMatterDetails, setSelectedMatterDetails] = useState<TrustEligibleMatterDto | null>(null);

  const handleSelectAll = (checked: boolean) => {
    if (isSelectAllPages && onSelectAllPages) {
      // If currently in "select all pages" mode, toggle it off
      onSelectAllPages(false);
    } else if (checked) {
      // Select all on current page
      const currentPageIds = matters.map(m => m.id);
      const newSelected = [...new Set([...selectedMatters, ...currentPageIds])];
      onSelectionChange(newSelected);
    } else {
      // Deselect all on current page
      const currentPageIds = new Set(matters.map(m => m.id));
      const newSelected = selectedMatters.filter(id => !currentPageIds.has(id));
      onSelectionChange(newSelected);
    }
  };

  const handleSelectMatter = (matterId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedMatters, matterId]);
    } else {
      onSelectionChange(selectedMatters.filter(id => id !== matterId));
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortField(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAmountChange = (matterId: string, amount: number) => {
    if (onAmountChange) {
      onAmountChange(matterId, amount);
    }
  };

  const handleShowDetails = (matter: TrustEligibleMatterDto) => {
    setSelectedMatterDetails(matter);
    setDetailsOpen(true);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <Icon name="HiArrowsUpDown" size="sm" className="ml-1" />;
    }
    if (sortDirection === 'asc') {
      return <Icon name="HiArrowUp" size="sm" className="ml-1" />;
    }
    if (sortDirection === 'desc') {
      return <Icon name="HiArrowDown" size="sm" className="ml-1" />;
    }
    return <Icon name="HiArrowsUpDown" size="sm" className="ml-1" />;
  };

  const sortedMatters = useMemo(() => {
    if (!sortField || !sortDirection) return matters;

    return [...matters].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [matters, sortField, sortDirection]);

  const currentPageSelected = matters.filter(m => selectedMatters.includes(m.id));
  const allCurrentPageSelected = matters.length > 0 && currentPageSelected.length === matters.length;
  const someCurrentPageSelected = currentPageSelected.length > 0 && currentPageSelected.length < matters.length;

  // For the checkbox state: if select all pages is active, show as checked
  // Otherwise, show based on current page selection
  const checkboxChecked = isSelectAllPages || allCurrentPageSelected;
  const checkboxIndeterminate = !isSelectAllPages && someCurrentPageSelected;

  return (
    <div className="overflow-x-auto max-w-full">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 relative table-fixed">
        <thead className="bg-white dark:bg-gray-800 sticky top-0 z-30">
          <tr>
            <th className="w-12 px-4 py-3 text-left">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={checkboxChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = checkboxIndeterminate;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="w-4 h-4 text-xtnd-blue bg-gray-100 border-gray-300 rounded focus:ring-xtnd-blue focus:ring-2"
                  title={isSelectAllPages ? `All ${totalRecords || 0} matters selected across all pages` : 'Select all matters on this page'}
                />
                {isSelectAllPages && (
                  <span className="text-xs text-xtnd-blue font-medium">
                    All pages
                  </span>
                )}
                {!isSelectAllPages && allCurrentPageSelected && totalRecords && totalRecords > matters.length && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => onSelectAllPages?.(true)}
                    className="text-xs text-xtnd-blue hover:text-xtnd-blue-700"
                  >
                    Select all {totalRecords} matters
                  </Button>
                )}
              </div>
            </th>
            <th className="w-32 px-4 py-3 text-left">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('matterDisplayNumber')}
                className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
              >
                Matter Number
                {getSortIcon('matterDisplayNumber')}
              </Button>
            </th>
            <th className="w-48 px-4 py-3 text-left">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('clientName')}
                className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
              >
                Client Name
                {getSortIcon('clientName')}
              </Button>
            </th>
            <th className="w-20 px-4 py-3 text-left">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('matterStatus')}
                className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
              >
                Status
                {getSortIcon('matterStatus')}
              </Button>
            </th>
            {visibleColumns.minimumThreshold && (
              <th className="w-28 px-4 py-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('minimumThreshold')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Min Threshold
                  {getSortIcon('minimumThreshold')}
                </Button>
              </th>
            )}
            {visibleColumns.trustBalance && (
              <th className="w-28 px-4 py-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('trustBalance')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Trust Balance
                  {getSortIcon('trustBalance')}
                </Button>
              </th>
            )}
            {visibleColumns.outstandingBalance && (
              <th className="w-28 px-4 py-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('outstandingBalance')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Outstanding
                  {getSortIcon('outstandingBalance')}
                </Button>
              </th>
            )}
            {visibleColumns.wip && (
              <th className="w-28 px-4 py-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('wip')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  WIP
                  {getSortIcon('wip')}
                </Button>
              </th>
            )}
            {visibleColumns.calculatedTrustAmount && (
              <th className="w-28 px-4 py-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('calculatedTrustAmount')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Calc Amount
                  {getSortIcon('calculatedTrustAmount')}
                </Button>
              </th>
            )}
            <th className="w-32 px-4 py-3 text-left">
              <span className="font-semibold text-xtnd-dark dark:text-xtnd-light">TR Amount</span>
            </th>
            {visibleColumns.issueDate && (
              <th className="w-48 px-4 py-3 text-left">
                <span className="font-semibold text-xtnd-dark dark:text-xtnd-light">Issue Date</span>
              </th>
            )}
            {visibleColumns.dueDate && (
              <th className="w-48 px-4 py-3 text-left">
                <span className="font-semibold text-xtnd-dark dark:text-xtnd-light">Due Date</span>
              </th>
            )}
            {visibleColumns.lastPaymentDate && (
              <th className="w-32 px-4 py-3 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('lastPaymentDate')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Last Payment
                  {getSortIcon('lastPaymentDate')}
                </Button>
              </th>
            )}
            {visibleColumns.lastInvoiceDate && (
              <th className="w-32 px-4 py-3 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('lastInvoiceDate')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Last Invoice
                  {getSortIcon('lastInvoiceDate')}
                </Button>
              </th>
            )}
            {visibleColumns.lastTrustRequestDate && (
              <th className="w-32 px-4 py-3 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('lastTrustRequestDate')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Last TR
                  {getSortIcon('lastTrustRequestDate')}
                </Button>
              </th>
            )}
            <th className="w-12 px-4 py-3 text-center">
              {/* Info button column */}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {sortedMatters.map((matter) => {
            const isSelected = selectedMatters.includes(matter.id);
            const displayAmount = editableAmounts[matter.id] ?? matter.calculatedTrustAmount;
            const inputValue = displayAmount !== undefined ? displayAmount.toFixed(2) : '';

            return (
              <tr
                key={matter.id}
                className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleSelectMatter(matter.id, e.target.checked)}
                    className="w-4 h-4 text-xtnd-blue bg-gray-100 border-gray-300 rounded focus:ring-xtnd-blue focus:ring-2"
                  />
                </td>
                <td className="px-4 py-3 font-medium text-xtnd-blue dark:text-xtnd-blue-300">
                  <a
                    href={`https://app.clio.com/nc/#/matters/${matter.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    title="View matter in Clio"
                  >
                    {matter.matterDisplayNumber}
                  </a>
                </td>
                <td className="px-4 py-3 text-xtnd-dark dark:text-xtnd-light">
                  <a
                    href={`https://app.clio.com/nc/#/contacts/${matter.clientId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    title="View contact in Clio"
                  >
                    {matter.clientName}
                  </a>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      matter.matterStatus === 'Open'
                        ? 'bg-xtnd-light text-xtnd-dark dark:bg-xtnd-blue-900 dark:text-xtnd-blue-200'
                        : 'bg-xtnd-dark-700 text-xtnd-white dark:bg-xtnd-dark-600 dark:text-xtnd-light'
                    }`}
                  >
                    {matter.matterStatus}
                  </span>
                </td>
                {visibleColumns.minimumThreshold && (
                  <td className="px-4 py-3 font-mono text-right text-xtnd-dark dark:text-xtnd-light">
                    {formatCurrency(matter.minimumThreshold)}
                  </td>
                )}
                {visibleColumns.trustBalance && (
                  <td className="px-4 py-3 font-mono text-right text-xtnd-dark dark:text-xtnd-light">
                    {formatCurrency(matter.trustBalance)}
                  </td>
                )}
                {visibleColumns.outstandingBalance && (
                  <td className="px-4 py-3 font-mono text-right text-xtnd-dark dark:text-xtnd-light">
                    {formatCurrency(matter.outstandingBalance)}
                  </td>
                )}
                {visibleColumns.wip && (
                  <td className="px-4 py-3 font-mono text-right text-xtnd-dark dark:text-xtnd-light">
                    {formatCurrency(matter.wip)}
                  </td>
                )}
                {visibleColumns.calculatedTrustAmount && (
                  <td className="px-4 py-3 font-mono text-right font-semibold text-xtnd-blue dark:text-xtnd-blue-300">
                    {formatCurrency(matter.calculatedTrustAmount)}
                  </td>
                )}
                <td className="px-4 py-3">
                  <Input
                    type="number"
                    value={inputValue}
                    onChange={(e) => handleAmountChange(matter.id, Number(e.target.value))}
                    className="w-32 text-right font-mono"
                    min="0"
                    step="0.01"
                  />
                </td>
                {visibleColumns.issueDate && (
                  <td className="px-4 py-3">
                    <div
                      className="date-picker-container"
                      style={{
                        width: '100%',
                        minWidth: '160px',
                        maxWidth: '100%',
                        position: 'relative'
                      }}
                    >
                      <DatePicker
                        mode="single"
                        value={editableDates[matter.id]?.issueDate}
                        onChange={(date) => onDateChange?.(matter.id, 'issueDate', date)}
                        className="date-picker-compact"
                      />
                      <style dangerouslySetInnerHTML={{
                        __html: `
                          .date-picker-container .date-picker-compact button span {
                            min-width: auto !important;
                            max-width: 140px !important;
                            overflow: hidden !important;
                            text-overflow: ellipsis !important;
                            white-space: nowrap !important;
                          }
                          .date-picker-container .date-picker-compact button {
                            width: 100% !important;
                            max-width: 100% !important;
                          }
                        `
                      }} />
                    </div>
                  </td>
                )}
                {visibleColumns.dueDate && (
                  <td className="px-4 py-3">
                    <div
                      className="date-picker-container"
                      style={{
                        width: '100%',
                        minWidth: '160px',
                        maxWidth: '100%',
                        position: 'relative'
                      }}
                    >
                      <DatePicker
                        mode="single"
                        value={editableDates[matter.id]?.dueDate}
                        onChange={(date) => onDateChange?.(matter.id, 'dueDate', date)}
                        className="date-picker-compact"
                      />
                      <style dangerouslySetInnerHTML={{
                        __html: `
                          .date-picker-container .date-picker-compact button span {
                            min-width: auto !important;
                            max-width: 140px !important;
                            overflow: hidden !important;
                            text-overflow: ellipsis !important;
                            white-space: nowrap !important;
                          }
                          .date-picker-container .date-picker-compact button {
                            width: 100% !important;
                            max-width: 100% !important;
                          }
                        `
                      }} />
                    </div>
                  </td>
                )}
                {visibleColumns.lastPaymentDate && (
                  <td className="px-4 py-3 text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                    {matter.lastPaymentDate
                      ? formatDate(matter.lastPaymentDate, DateFormat.US)
                      : 'No payments'}
                  </td>
                )}
                {visibleColumns.lastInvoiceDate && (
                  <td className="px-4 py-3 text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                    {matter.lastInvoiceDate
                      ? formatDate(matter.lastInvoiceDate, DateFormat.US)
                      : 'No invoices'}
                  </td>
                )}
                {visibleColumns.lastTrustRequestDate && (
                  <td className="px-4 py-3 text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                    {matter.lastTrustRequestDate
                      ? formatDate(matter.lastTrustRequestDate, DateFormat.US)
                      : 'No TRs'}
                  </td>
                )}
                <td className="px-4 py-3 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleShowDetails(matter)}
                    className="h-10 w-10 p-0"
                    title="View matter details"
                  >
                    <Icon name="InfoIcon" size="sm" />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {matters.length === 0 && (
        <div className="p-8 text-center text-xtnd-dark-600 dark:text-xtnd-light-400">
          <Icon name="InfoIcon" size="xl" className="mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2 text-xtnd-dark dark:text-xtnd-light">No matters found</h3>
          <p>Try adjusting your filters to see more results.</p>
        </div>
      )}

      {/* Matter Details Slide-out Panel */}
      <SlideOutPanel
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title="Matter Details"
        width="md"
      >
        <SlideOutPanelBody>
          {selectedMatterDetails && (
            <div className="space-y-6">
              {/* Matter Information */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Matter Number</p>
                  <p className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                    <a
                      href={`https://app.clio.com/nc/#/matters/${selectedMatterDetails.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                      title="View matter in Clio"
                    >
                      {selectedMatterDetails.matterDisplayNumber}
                    </a>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Client</p>
                  <p className="font-medium text-xtnd-dark dark:text-xtnd-light">
                    <a
                      href={`https://app.clio.com/nc/#/contacts/${selectedMatterDetails.clientId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                      title="View contact in Clio"
                    >
                      {selectedMatterDetails.clientName}
                    </a>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Status</p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedMatterDetails.matterStatus === 'Open'
                        ? 'bg-xtnd-light text-xtnd-dark dark:bg-xtnd-blue-900 dark:text-xtnd-blue-200'
                        : 'bg-xtnd-dark-700 text-xtnd-white dark:bg-xtnd-dark-600 dark:text-xtnd-light'
                    }`}
                  >
                    {selectedMatterDetails.matterStatus}
                  </span>
                </div>
              </div>

              {/* Trust Calculation Details */}
              <div className="rounded-lg bg-xtnd-light dark:bg-xtnd-dark-700 p-4 space-y-3">
                <h4 className="font-semibold text-sm text-xtnd-dark dark:text-xtnd-light">Trust Calculation</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Minimum Threshold</span>
                    <span className="font-mono font-medium text-xtnd-dark dark:text-xtnd-light">
                      {formatCurrency(selectedMatterDetails.minimumThreshold)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Trust Balance</span>
                    <span className="font-mono font-medium text-xtnd-dark dark:text-xtnd-light">
                      {formatCurrency(selectedMatterDetails.trustBalance)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Outstanding Balance</span>
                    <span className="font-mono font-medium text-xtnd-dark dark:text-xtnd-light">
                      {formatCurrency(selectedMatterDetails.outstandingBalance)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">WIP Balance</span>
                    <span className="font-mono font-medium text-xtnd-dark dark:text-xtnd-light">
                      {formatCurrency(selectedMatterDetails.wip)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-xtnd-dark-200 dark:border-xtnd-dark-600">
                    <span className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Calculated Amount</span>
                    <span className="font-mono font-bold text-xtnd-blue dark:text-xtnd-blue-300">
                      {formatCurrency(selectedMatterDetails.calculatedTrustAmount)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Activity Dates */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Last Payment</span>
                  <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                    {selectedMatterDetails.lastPaymentDate
                      ? formatDate(selectedMatterDetails.lastPaymentDate, DateFormat.US)
                      : 'No payments'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Last Invoice</span>
                  <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                    {selectedMatterDetails.lastInvoiceDate
                      ? formatDate(selectedMatterDetails.lastInvoiceDate, DateFormat.US)
                      : 'No invoices'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </SlideOutPanelBody>
      </SlideOutPanel>
    </div>
  );
}

